import { createContext, useContext, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import "@/App.css";
import api from "@/lib/api";

import HomePage from "@/pages/HomePage";
import ProductsPage from "@/pages/ProductsPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import CartPage from "@/pages/CartPage";
import LegalPage from "@/pages/LegalPage";
import AccountContinuePage from "@/pages/checkout/AccountContinuePage";
import CheckoutReadyPage from "@/pages/checkout/CheckoutReadyPage";
import CheckoutResultPage from "@/pages/checkout/CheckoutResultPage";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminProductEdit from "@/pages/admin/AdminProductEdit";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminMessages from "@/pages/admin/AdminMessages";
import LoadingScreen from "@/components/LoadingScreen";
import RouteMeta from "@/components/RouteMeta";
import { Toaster } from "@/components/ui/sonner";
import { CommerceProvider } from "@/contexts/CommerceContext";


const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem("admin_token"));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const verifyToken = async () => {
      if (!token) {
        if (active) setIsLoading(false);
        return;
      }
      try {
        await api.get("/admin/verify");
        if (active) setIsAuthenticated(true);
      } catch (error) {
        localStorage.removeItem("admin_token");
        if (active) {
          setToken(null);
          setIsAuthenticated(false);
        }
      } finally {
        if (active) setIsLoading(false);
      }
    };
    verifyToken();
    return () => {
      active = false;
    };
  }, [token]);

  const login = (newToken) => {
    localStorage.setItem("admin_token", newToken);
    setToken(newToken);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ token, isAuthenticated, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <CommerceProvider>
          <div className="App min-h-screen bg-background">
            <div className="scanline-overlay" />
          <RouteMeta />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/product/:id" element={<ProductDetailPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/terms" element={<LegalPage policy="terms" />} />
            <Route path="/privacy" element={<LegalPage policy="privacy" />} />
            <Route path="/refund-policy" element={<LegalPage policy="refund" />} />
            <Route path="/account/continue" element={<AccountContinuePage />} />
            <Route path="/checkout/continue" element={<Navigate to="/products" replace />} />
            <Route path="/checkout/ready" element={<CheckoutReadyPage />} />
            <Route path="/checkout/complete" element={<CheckoutResultPage />} />
            <Route path="/checkout/cancel" element={<CheckoutResultPage cancelled />} />
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/products" element={<ProtectedRoute><AdminProducts /></ProtectedRoute>} />
            <Route path="/admin/products/new" element={<ProtectedRoute><AdminProductEdit /></ProtectedRoute>} />
            <Route path="/admin/products/:id" element={<ProtectedRoute><AdminProductEdit /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
            <Route path="/admin/messages" element={<ProtectedRoute><AdminMessages /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
            <Toaster position="top-right" richColors />
          </div>
        </CommerceProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
